import type { ComponentType, ReactNode } from 'react';

export interface NavIconProps {
  size?: number | string;
  color?: string;
  variant?: 'Linear' | 'Outline' | 'Broken' | 'Bold' | 'Bulk' | 'TwoTone';
}

export interface NavItem {
  id: string;
  label: string;
  /** Route path. Items with `children` usually omit it (they are disclosure groups). */
  href?: string;
  icon?: ComponentType<NavIconProps>;
  children?: NavItem[];
  /** Small trailing badge (e.g. a count). Keep it short; it is read after the label. */
  badge?: ReactNode;
}

export interface NavSection {
  id: string;
  /** Optional section heading (hidden in the collapsed rail). */
  label?: string;
  items: NavItem[];
}

/** True when `pathname` equals `href` or is nested below it (`href + '/'`). */
export function matchesHref(href: string, pathname: string): boolean {
  if (!href) return false;
  const normalized = href.length > 1 && href.endsWith('/') ? href.slice(0, -1) : href;
  return pathname === normalized || pathname.startsWith(`${normalized}/`);
}

/**
 * The single active item: the one whose href matches `pathname` most specifically, so that
 * "/students/import" wins over "/students".
 */
export function findActiveItemId(
  sections: NavSection[],
  pathname: string | undefined,
): string | null {
  if (!pathname) return null;
  let bestId: string | null = null;
  let bestLength = -1;
  const stack: NavItem[] = sections.flatMap((section) => section.items);
  for (let item = stack.pop(); item; item = stack.pop()) {
    if (item.href && matchesHref(item.href, pathname) && item.href.length > bestLength) {
      bestId = item.id;
      bestLength = item.href.length;
    }
    if (item.children) stack.push(...item.children);
  }
  return bestId;
}

export function containsItem(item: NavItem, id: string | null): boolean {
  if (!id || !item.children) return false;
  return item.children.some((child) => child.id === id || containsItem(child, id));
}
